import { dateFormat, flatJoin, numberFormat } from "shared/util"

import {
  SisetuResult,
  AmbulanceResult,
	useGetSisetu,
	useListChildrenSisetu
} from 'interface/sisetu'

import { Section, SectionProps } from 'components/controls/section'

import {
  Stack, Box, BoxProps, Typography, Link, Skeleton,
  List, ListItem, ListItemProps, ListItemButton
} from "@mui/material"

import { BlockCaption } from "components/controls/blockCaption"
import { ValueSpan } from "components/controls/valueSpan"

import { WorkerCountTable, workerCountTotal } from "./workerCountTable"
import { useMemo } from "react"

const googleMapUrl = 'https://www.google.com/maps?q='

const ambulanceTpotal = (ambulance: AmbulanceResult) => {
  return (
    (ambulance.kyukyuHolidayNumberOfPatients ?? 0) +
    (ambulance.kyukyuNighttimeNumberOfPatients ?? 0) +
    (ambulance.numberOfAmbulanceAcceptance ?? 0)
  )
}

export const useOyaDaigaku = (
	sisetu: SisetuResult
) => {
	const oyaDaigakuCode = useMemo(
		() => (sisetu.oyaDaigakuCode && sisetu.oyaDaigakuCode !== sisetu.sisetuCode) ? sisetu.oyaDaigakuCode : null,
		[sisetu]
	)
	const oyaDaigakuState = useGetSisetu(null, oyaDaigakuCode)
	const oyaDaigaku = useMemo(
		() => {
			if (!oyaDaigakuState.data) return null
			return oyaDaigakuState.data.sisetuCode === sisetu.sisetuCode ? null : oyaDaigakuState.data
		},
		[oyaDaigakuState.data, sisetu.sisetuCode]
	)
	return {
		...oyaDaigakuState,
		data: oyaDaigaku
	}
}

export const useChildrenSisetu = (
	sisetu: SisetuResult
) => {
	const childrenState = useListChildrenSisetu(sisetu.sisetuCode)
	const children = useMemo(
		() => {
			if (!childrenState.data) return []
			const children = childrenState.data.list
			children.sort((a, b) => {
				const bedCountA = (a.bedCount?.total ?? 0)
				const bedCountB = (b.bedCount?.total ?? 0)
				if (bedCountA !== bedCountB) return bedCountB - bedCountA
				return (a.sisetuKubun?.code ?? '').localeCompare(b.sisetuKubun?.code ?? '')
			})
			return children
		},
		[childrenState.data]
	)
	return {
		...childrenState,
		data: children
	}
}

type SisetuListItemProps = {
	sisetu: SisetuResult
  onSisetuOpen: (sisetuCode: string) => void
} & ListItemProps
const SisetuListItem = ({
	sisetu,
	onSisetuOpen,
	...others
}: SisetuListItemProps) => {
	return (
		<ListItem {...others} disablePadding disableGutters>
			<ListItemButton
				disableRipple
				sx={{
					alignItems: 'flex-start',
					paddingY: 0.5
				}}
				onClick={() => onSisetuOpen(sisetu.sisetuCode)}
			>
				<Stack direction="row" width="100%" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
					<Typography variant="body2" component="span" color="text.primary">{sisetu.sisetuName}</Typography>
					{(sisetu.sisetuKubun?.name || sisetu.bedCount?.total) ? (
						<Typography variant="caption" color="text.secondary">（{
							flatJoin(' ', sisetu.sisetuKubun?.name, sisetu.bedCount?.total ? `${sisetu.bedCount.total}床` : null)
						}）</Typography>
					) : <></>}
				</Stack>
			</ListItemButton>
		</ListItem>
	)	
}

type SisetuBlockProps = {
	sisetu: SisetuResult
	oyaDaigaku: ReturnType<typeof useOyaDaigaku>
	childrenSisetu: ReturnType<typeof useChildrenSisetu>
	isSmallMedia: boolean
	sectionWidth: SectionProps["labelWidth"]
  onSisetuOpen: (sisetuCode: string) => void
} & BoxProps
export const SisetuBlock = ({
	sisetu,
	oyaDaigaku,
	childrenSisetu,
	isSmallMedia,
	sectionWidth,
	onSisetuOpen,
	...others
}: SisetuBlockProps) => {
	return (
		<Box {...others}>
			<BlockCaption
				caption="施設情報"
				rightElement={
					<>
						<Typography variant="caption" color='text.primary' component="div">DCF施設コード: {sisetu.sisetuCode}</Typography>
						{ sisetu.institutionalCode ? (
							<>
								<Typography variant="caption" color='text.secondary' component="div">/</Typography>
								<Typography variant="caption" color='text.primary' component="div">厚労省施設コード: {sisetu.institutionalCode}</Typography>
							</>
						) : <></> }
					</>
				}
			/>
			{(sisetu.oyaDaigakuCode && sisetu.oyaDaigakuCode !== sisetu.sisetuCode) ? (
				<Section label="親大学" labelWidth={sectionWidth} disablePadding innerProps={{ paddingY: 0.5 }}>
					<List disablePadding>
						{oyaDaigaku.loading && (
							<ListItem disablePadding disableGutters sx={{ paddingX: 1 }}>
								<Skeleton variant="text" width="100%" sx={{ fontSize: '1.2rem' }} />
							</ListItem>
						)}
						{oyaDaigaku.data && <SisetuListItem sisetu={oyaDaigaku.data} onSisetuOpen={onSisetuOpen}/>}
					</List>
				</Section>
			) : <></>}

			{(childrenSisetu.data.length > 0) ? (
				<Section label={`附属施設\n(${childrenSisetu.data.length} 施設)`} labelWidth={sectionWidth} disablePadding innerProps={{ paddingY: 0.5 }}>
					<List disablePadding>
						{childrenSisetu.data.map((child, i) => (
							<SisetuListItem key={`child-${sisetu.sisetuCode}-${i}`} sisetu={child} onSisetuOpen={onSisetuOpen}/>
						))}
					</List>
				</Section>
			) : <></>}

			{ sisetu.iryoukenName ? (
				<Section label="医療圏" labelWidth={sectionWidth}>
					<Stack direction="row" useFlexGap>
						<Typography variant="body2">{sisetu.juusyoKenName}</Typography>
						{sisetu.cityName ? (
							<Typography variant="body2" marginLeft={1}>{sisetu.cityName}</Typography>
						) : <></>}
						{sisetu.iryoukenName ? (
							<Typography variant="body2">（{sisetu.iryoukenName}）</Typography>
						) : <></>}
					</Stack>
				</Section>
			) : <></> }

			{ sisetu.juusyo ? (
				<Section label="住所" labelWidth={sectionWidth}>
					<Box>
						{sisetu.zip ? (
							<Link target="_blank" underline='hover' href={`${googleMapUrl}/〒${sisetu.zip} ${sisetu.juusyo}`} variant='body2'>〒{sisetu.zip} {sisetu.juusyo}</Link>
						) : (
							<Link target="_blank" underline='hover' href={`${googleMapUrl}/${sisetu.juusyo}`} variant='body2'>{sisetu.juusyo}</Link>
						)}
					</Box>
				</Section>
			) : <></> }

			{ sisetu.tel ? (
				<Section label="電話番号" labelWidth={sectionWidth}>
					<Box><Link target="_blank" underline='hover' href={`tel:${sisetu.tel}`} variant='body2'>{sisetu.tel}</Link></Box>
				</Section>
			) : <></> }

			{ sisetu.url ? (
				<Section label="Link" labelWidth={sectionWidth}>
					<Box><Link target="_blank" underline='hover' href={sisetu.url} variant='body2'>WebPage</Link></Box>
				</Section>
			) : <></> }

			{(sisetu.bedCount && (sisetu.bedCount.total ?? 0) > 0) ? (
				<Section label="病床数" labelWidth={sectionWidth} innerProps={{ paddingY: 0.5 }}>
					<Stack direction="row" spacing={2} flexWrap="wrap">
						{(sisetu.bedCount.ippan ?? 0) > 0 ? (
							<ValueSpan text={numberFormat(sisetu.bedCount.ippan)} prefix="一般" suffix="床" />
						) : <></>}

						{(sisetu.bedCount.seisin ?? 0) > 0 ? (
							<ValueSpan text={numberFormat(sisetu.bedCount.seisin)} prefix="精神" suffix="床" />
						) : <></>}

						{(sisetu.bedCount.kekkaku ?? 0) > 0 ? (
							<ValueSpan text={numberFormat(sisetu.bedCount.kekkaku)} prefix="結核" suffix="床" />
						) : <></>}

						{(sisetu.bedCount.kansen ?? 0) > 0 ? (
							<ValueSpan text={numberFormat(sisetu.bedCount.kansen)} prefix="感染" suffix="床" />
						) : <></>}

						{(sisetu.bedCount.ryouyou ?? 0) > 0 ? (
							<ValueSpan text={numberFormat(sisetu.bedCount.ryouyou)} prefix="療養" suffix="床" />
						) : <></>}
					</Stack>
				</Section>
			) : <></>}

			{sisetu.workerCounts && workerCountTotal(sisetu.workerCounts) > 0 ? (
				<Section
					labelWidth={sectionWidth}
					label="職員数"
					innerProps={ isSmallMedia ? {
						paddingLeft: 0,
						paddingRight: 1
					} : undefined}
				>
					<WorkerCountTable isSmallMedia={isSmallMedia} workerCounts={sisetu.workerCounts} />
				</Section>
			) : <></>}

			{sisetu.ambulance && ambulanceTpotal(sisetu.ambulance) > 0 ? (
				<Section labelWidth={sectionWidth} label="外来機能" innerProps={{ paddingY: 0.5 }}>
					<Stack
						direction={isSmallMedia ? "column" : "row"}
						spacing={isSmallMedia ? 0 : 2}
					>
						{ sisetu.ambulance.kyukyuHolidayNumberOfPatients ? (
							<ValueSpan
								align="left"
								minWidth={isSmallMedia ? "4em" : undefined}
								textAlign="right"
								text={numberFormat(sisetu.ambulance.kyukyuHolidayNumberOfPatients)}
								prefix="休日診療"
								prefixProps={isSmallMedia ? { width: '5rem' } : undefined}
								suffix="件"
							/>
						) : <></> }

						{ sisetu.ambulance.kyukyuNighttimeNumberOfPatients ? (
							<ValueSpan
								align="left"
								minWidth={isSmallMedia ? "4em" : undefined}
								textAlign="right"
								text={numberFormat(sisetu.ambulance.kyukyuNighttimeNumberOfPatients)}
								prefix="夜間/時間外"
								prefixProps={isSmallMedia ? { width: '5rem' } : undefined}
								suffix="件"
							/>
						) : <></> }

						{ sisetu.ambulance.numberOfAmbulanceAcceptance ? (
							<ValueSpan
								align="left"
								minWidth={isSmallMedia ? "4em" : undefined}
								textAlign="right"
								text={numberFormat(sisetu.ambulance.numberOfAmbulanceAcceptance)}
								prefix="救急受入"
								prefixProps={isSmallMedia ? { width: '5rem' } : undefined}
								suffix="件"
							/>
						) : <></> }
					</Stack>
				</Section>
			) : <></>}

			{ sisetu.daihyousya ? (
				<Section labelWidth={sectionWidth} label="代表者">
					<Typography variant="body2" component="div">{sisetu.daihyousya}</Typography>
				</Section>
			) : <></> }

			{ sisetu.establisher ? (
				<Section labelWidth={sectionWidth} label="開業者">
					<Typography variant="body2" component="div">{sisetu.establisher}</Typography>
				</Section>
			) : <></> }
			
			{ sisetu.openingDate ? (
				<Section labelWidth={sectionWidth} label="開院日">
					<Typography variant="body2" component="div">{dateFormat(sisetu.openingDate)}</Typography>
				</Section>
			) : <></> }
		</Box>
	)
}